// src/hooks/useTyping.js
// "Typing…" indicator using Supabase Realtime broadcast: nothing is stored in
// the database and it costs nothing. roomId = the member's user id (both sides
// of one conversation join the same room).
import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const SHOW_FOR_MS    = 3500 // hide after this long with no new "typing" signal
const SEND_EVERY_MS  = 2500 // don't spam: at most one signal per 2.5s

export default function useTyping(roomId, myId) {
  const [otherTyping, setOtherTyping] = useState(false)
  const channelRef = useRef(null)
  const readyRef   = useRef(false)
  const lastSent   = useRef(0)
  const hideTimer  = useRef(null)

  useEffect(() => {
    if (!roomId || !myId) return
    const channel = supabase
      .channel(`typing-${roomId}`)
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (!payload || payload.from === myId) return
        setOtherTyping(true)
        clearTimeout(hideTimer.current)
        hideTimer.current = setTimeout(() => setOtherTyping(false), SHOW_FOR_MS)
      })
      .subscribe(status => { readyRef.current = status === 'SUBSCRIBED' })
    channelRef.current = channel
    return () => {
      clearTimeout(hideTimer.current)
      readyRef.current = false
      channelRef.current = null
      setOtherTyping(false)
      supabase.removeChannel(channel)
    }
  }, [roomId, myId])

  // Call on every keystroke — it throttles itself
  const notifyTyping = useCallback(() => {
    const now = Date.now()
    if (!readyRef.current || !channelRef.current || now - lastSent.current < SEND_EVERY_MS) return
    lastSent.current = now
    channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { from: myId } })
  }, [myId])

  // The other person just sent their message — they're obviously not typing anymore
  const clearTyping = useCallback(() => {
    clearTimeout(hideTimer.current)
    setOtherTyping(false)
  }, [])

  return { otherTyping, notifyTyping, clearTyping }
}
